import { useCallback, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';

interface AutoSaveData<T> {
  data: T;
  activeStep: number;
  timestamp: number;
}

interface UseFormAutoSaveOptions {
  /** Unique identifier for the user (e.g., employer ID) */
  userId: number | string | undefined;
  /** Base key for storage (will be combined with userId) */
  storageKey: string;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Fields to exclude from saving (e.g., ['payment']) */
  excludeFields?: string[];
}

interface UseFormAutoSaveReturn<T> {
  /** Save data to sessionStorage (debounced) */
  save: (data: T, activeStep: number) => void;
  /** Immediately save data without debounce */
  saveImmediate: (data: T, activeStep: number) => void;
  /** Restore saved data from sessionStorage */
  restore: () => AutoSaveData<T> | null;
  /** Clear saved data from sessionStorage */
  clear: () => void;
  /** Check if a draft exists in sessionStorage */
  hasDraft: () => boolean;
}

export function useFormAutoSave<T extends object>({
  userId,
  storageKey,
  debounceMs = 500,
  excludeFields = [],
}: UseFormAutoSaveOptions): UseFormAutoSaveReturn<T> {
  const getKey = useCallback(() => {
    if (!userId) return null;
    return `${storageKey}_${userId}`;
  }, [userId, storageKey]);

  const filterData = useCallback(
    (data: T): Partial<T> => {
      if (!data || typeof data !== 'object') return data;

      const filtered = { ...data };
      for (const field of excludeFields) {
        delete filtered[field as keyof T];
      }
      return filtered;
    },
    [excludeFields],
  );

  const saveToStorage = useCallback(
    (data: T, activeStep: number) => {
      const key = getKey();
      if (!key || typeof window === 'undefined') return;

      try {
        const filteredData = filterData(data);
        const saveData: AutoSaveData<Partial<T>> = {
          data: filteredData,
          activeStep,
          timestamp: Date.now(),
        };
        window.sessionStorage.setItem(key, JSON.stringify(saveData));
      } catch (error) {
        console.warn(`Error saving to sessionStorage key "${key}":`, error);
      }
    },
    [getKey, filterData],
  );

  // Create debounced save function
  const debouncedSaveRef = useRef(
    debounce((data: T, activeStep: number) => {
      saveToStorage(data, activeStep);
    }, debounceMs),
  );

  // Update debounced function if dependencies change
  useEffect(() => {
    debouncedSaveRef.current = debounce((data: T, activeStep: number) => {
      saveToStorage(data, activeStep);
    }, debounceMs);

    return () => {
      debouncedSaveRef.current.cancel();
    };
  }, [saveToStorage, debounceMs]);

  const save = useCallback((data: T, activeStep: number) => {
    debouncedSaveRef.current(data, activeStep);
  }, []);

  const saveImmediate = useCallback(
    (data: T, activeStep: number) => {
      debouncedSaveRef.current.cancel();
      saveToStorage(data, activeStep);
    },
    [saveToStorage],
  );

  const restore = useCallback((): AutoSaveData<T> | null => {
    const key = getKey();
    if (!key || typeof window === 'undefined') return null;

    try {
      const item = window.sessionStorage.getItem(key);
      if (!item) return null;

      return JSON.parse(item) as AutoSaveData<T>;
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error);
      return null;
    }
  }, [getKey]);

  const clear = useCallback(() => {
    const key = getKey();
    if (!key || typeof window === 'undefined') return;

    try {
      debouncedSaveRef.current.cancel();
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Error clearing sessionStorage key "${key}":`, error);
    }
  }, [getKey]);

  const hasDraft = useCallback((): boolean => {
    const key = getKey();
    if (!key || typeof window === 'undefined') return false;

    try {
      return window.sessionStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  }, [getKey]);

  return {
    save,
    saveImmediate,
    restore,
    clear,
    hasDraft,
  };
}

export default useFormAutoSave;
